import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment';
import { asapScheduler, combineLatest, interval, merge, Observable, scheduled, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AddOnDetail } from 'src/app/classes/add-on-detail';
import { DriverTask } from 'src/app/classes/driver-task';
import { Events } from 'src/app/classes/events';
import { Passenger } from 'src/app/classes/passenger';
import { Profile } from 'src/app/classes/profile';
import { EVENT_OUTBOUND_TIMER } from 'src/app/common/constants';
import { RampGroup } from 'src/app/common/ramp-group';
import { TaskType } from 'src/app/common/task-types';
import { EventsDeltaService } from 'src/app/services/events-delta.service';
import { EventsService } from 'src/app/services/events.service';
import { OutboundService } from 'src/app/services/outbound.service';
import { ProfileService } from 'src/app/services/profile.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  standalone: false,
  selector: 'app-outbound',
  templateUrl: './outbound.component.html',
  styleUrls: [ './outbound.component.scss' ]
})
export class OutboundComponent implements OnInit, OnDestroy {
  rampGroups: RampGroup[] = [ '2-4-6', '1-3', '5-7', '9-11', '21-23' ];

  selectedOutboundTasks: DriverTask[] = [];
  outboundDropoffTasks: DriverTask[] = [];
  expandedOutbound: DriverTask;

  disableAssignPickupTasks = false;

  outboundTaskMap: { [key: string]: DriverTask[] } = this.initOutboundTaskMap();

  subManager: Subscription = new Subscription();
  eventsSubscription: Subscription;

  constructor(
    readonly eventsService: EventsService,
    readonly eventsDeltaService: EventsDeltaService,
    readonly outboundService: OutboundService,
    readonly profileService: ProfileService,
    readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.eventsDeltaService.stopWorkerInterval();
    this.subManager.add(this.subToEventsAndDeltas());
    this.eventsService.triggerEventsUpdate();
    const subInterval = interval(EVENT_OUTBOUND_TIMER);
    this.eventsSubscription = subInterval.subscribe(() => this.eventsService.triggerEventsUpdate());
  }

  ngOnDestroy(): void {
    this.subManager.unsubscribe();
    this.eventsSubscription.unsubscribe();
    this.eventsDeltaService.startEventDeltaInterval();
  }

  initOutboundTaskMap(): { [key: string]: DriverTask[] } {
    const outboundTaskMap = {};
    this.rampGroups.forEach((ramp: RampGroup) => (outboundTaskMap[ramp] = []));

    return outboundTaskMap;
  }

  subToEventsAndDeltas(): Subscription {
    return combineLatest([
      this.profileService.getProfileObs(),
      merge(this.eventsService.getEventData())
      // merge(this.eventsService.getEventData(), this.eventsDeltaService.getEventData())
    ]).subscribe(([ profile, events ]: [Profile, Events]) => {
      // Filter for drop off tasks that are assigned to currently logged in user
      if (events && events.driverTasks && events.driverTasks.length) {
        this.outboundDropoffTasks = events.driverTasks
          .filter((task: DriverTask) => task.assignTo === profile.adId && task.status === 'Started' && task.typeName === TaskType.OUTBOUND_FLIGHT)
          .map((task: DriverTask) => ({
            ...task,
            passengers: (task.passengers || []).map((passenger: Passenger) => ({ ...passenger, selected: true })),
            addOnDetails: (task.addOnDetails || [])
              .map((addOnDetail: AddOnDetail) => ({ ...addOnDetail, selected: true }))
          }));
        this.outboundDropoffTasks.sort(this.outboundSortFn);
      }

      // Distribute and sort unassigned outbound tasks based on ramp groups
      if (events && events.unassignedTasks && events.unassignedTasks.length) {
        const unassignedTaskMap: { [key: string]: DriverTask[] } = this.initOutboundTaskMap();

        events.unassignedTasks
          .filter((task: DriverTask) => task.typeName === TaskType.OUTBOUND_FLIGHT)
          .forEach((outbound: DriverTask) => {
            this.mapOutbound(outbound, unassignedTaskMap);
          });

        Object.keys(unassignedTaskMap).forEach((rampGroup: RampGroup) =>
          this.outboundTaskMap[rampGroup].sort(this.outboundSortFn)
        );

        this.outboundTaskMap = unassignedTaskMap;
      }
    });
  }

  // Maps outbound task to ramp group based on parking position
  mapOutbound(outbound: DriverTask, taskMap: { [key: string]: DriverTask[] }): void {
    switch (this.getRamp(outbound)) {
      case '02':
      case '04':
      case '06':
        taskMap['2-4-6'].push(outbound);
        break;
      case '01':
      case '03':
        taskMap['1-3'].push(outbound);
        break;
      case '05':
      case '07':
        taskMap['5-7'].push(outbound);
        break;
      case '09':
      case '11':
        taskMap['9-11'].push(outbound);
        break;
      case '21':
      case '23':
        taskMap['21-23'].push(outbound);
        break;
    }
  }

  // Sorts outbound tasks by departure time
  outboundSortFn(a: DriverTask, b: DriverTask): number {
    return moment(a.departureTime).diff(moment(b.departureTime));
  }

  // Determines the ramp based on the first two digits of the parking position
  getRamp(outbound: DriverTask): string {
    const parkingPosition: string = outbound.parkingPosition;
    return parkingPosition && parkingPosition.length >= 2 ? parkingPosition.substring(0, 2) : '';
  }

  // Stage the outbound task in the "selected" section for assigning/picking up
  selectOutbound(outbound: DriverTask, ramp: RampGroup): void {
    this.selectedOutboundTasks.push(outbound);
    this.outboundTaskMap[ramp].splice(this.outboundTaskMap[ramp].indexOf(outbound), 1);
  }

  // Remove the outbound task from the "selected" section
  unselectOutbound(outbound: DriverTask): void {
    this.mapOutbound(outbound, this.outboundTaskMap);
    Object.keys(this.outboundTaskMap).forEach((rampGroup: RampGroup) =>
      this.outboundTaskMap[rampGroup].sort(this.outboundSortFn)
    );
    this.selectedOutboundTasks.splice(this.selectedOutboundTasks.indexOf(outbound), 1);
  }

  // Set the currently expanded outbound task. This controls the expanded task for all the sections (selected, dropoffs, unassigned) so that only one task is expanded at a time
  expandOutbound(outbound: DriverTask): void {
    this.expandedOutbound = this.expandedOutbound === outbound ? undefined : outbound;
  }

  // Assign/pickup the selected tasks and calls the EventsDeltaService to refresh the list
  assignPickupTasks(tasks: DriverTask[]): void {
    this.disableAssignPickupTasks = true;
    this.outboundService.assignPickupOutboundTasks(tasks).pipe(catchError(this.httpErrorHandler)).subscribe(() => {
      // this.eventsDeltaService.getDeltas();
      this.eventsService.triggerEventsUpdate();
      this.selectedOutboundTasks = [];
      this.disableAssignPickupTasks = false;
    });
  }

  // Drop off the specified task and calls the EventsDeltaService to refresh the list
  dropOffTask(task: DriverTask): void {
    this.outboundService
      .dropOffTask(task as DriverTask)
      .pipe(tap(() => this.eventsService.triggerEventsUpdate()), catchError(this.httpErrorHandler))
      // .pipe(tap(() => this.eventsDeltaService.getDeltas()), catchError(this.httpErrorHandler))
      .subscribe(() => this.outboundDropoffTasks.splice(this.outboundDropoffTasks.indexOf(task), 1));
      // console.log(task.taskName);
  }

  // Standard error handler using the ToastService
  httpErrorHandler(err: HttpErrorResponse): Observable<any> {
    this.toastService.show(`Error with outbound task request: ${err.statusText}`, {
      classname: 'bg-danger text-light',
      delay: 4000
    });
    return scheduled([], asapScheduler);
  }
}
